import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { PlayArrow, Pause, SkipNext, Close, Replay } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { canPreview, previewKey, webPreviewPlayer } from './previewPlayer';

export const useWebPreview = () =>
  useSyncExternalStore(
    webPreviewPlayer.subscribe,
    webPreviewPlayer.getSnapshot,
    webPreviewPlayer.getSnapshot,
  );
export function PreviewButton({ track, queue, owner, onPlay, showProgress = false }) {
  const state = useWebPreview();
  const { t } = useWebLocale();
  const available = canPreview(track);
  const active = state.track && previewKey(state.track) === previewKey(track);
  const running = active && ['loading', 'playing'].includes(state.status);
  const label = `${t(!available ? 'previewUnavailable' : running ? 'pausePreview' : 'playPreview')} · ${track.title}`;
  return (
    <IconButton
      aria-label={label}
      title={label}
      disabled={!available}
      sx={{ width: 44, height: 44, flexShrink: 0, color: active ? '#f16438' : 'text.primary' }}
      onClick={(event) => {
        event.stopPropagation();
        if (active) webPreviewPlayer.toggle();
        else {
          onPlay?.();
          void webPreviewPlayer.play(track, queue, owner);
        }
      }}
    >
      {showProgress && active && <PlaybackRing />}
      {running ? <Pause /> : <PlayArrow />}
    </IconButton>
  );
}
export function PlaybackRing() {
  const { t } = useWebLocale();
  const progress = useSyncExternalStore(
    webPreviewPlayer.subscribeProgress,
    webPreviewPlayer.getProgress,
    webPreviewPlayer.getProgress,
  );
  const value =
    progress.duration > 0 ? Math.min(1, Math.max(0, progress.current / progress.duration)) : 0;
  return (
    <svg
      className="pp-playback-ring"
      viewBox="0 0 40 40"
      role="progressbar"
      aria-label={t('previewProgress')}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
    >
      <circle
        cx="20"
        cy="20"
        r="17"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth="2"
      />
      <circle
        className="pp-playback-ring-value"
        cx="20"
        cy="20"
        r="17"
        fill="none"
        stroke="#f16438"
        strokeWidth="2.5"
        strokeLinecap="round"
        pathLength="100"
        strokeDasharray="100"
        strokeDashoffset={100 * (1 - value)}
        transform="rotate(-90 20 20)"
      />
    </svg>
  );
}
function PlayerArtwork({ src }) {
  const [failed, setFailed] = useState(false);
  return src && !failed ? (
    <img className="pp-preview-artwork" src={src} alt="" onError={() => setFailed(true)} />
  ) : (
    <span className="pp-preview-artwork pp-preview-artwork-fallback" aria-hidden="true">
      p.
    </span>
  );
}

export function WebPreviewPlayer() {
  const state = useWebPreview();
  const { t } = useWebLocale();
  useEffect(() => webPreviewPlayer.attach(), []);
  if (!state.track || state.miniSuppressed) return null;
  const running = ['playing', 'loading'].includes(state.status);
  const track = state.track;
  const path = track.albumSlug
    ? `/reviews/${encodeURIComponent(track.albumSlug)}`
    : track.albumId
      ? `/reviews/local/${encodeURIComponent(track.albumId)}`
      : null;
  return (
    <Box className="pp-preview-player" role="region" aria-label={t('preview')}>
      <PlayerArtwork key={track.artworkUrl || 'fallback'} src={track.artworkUrl} />
      <div className="pp-preview-description">
        {path ? <Link to={path}>{track.title}</Link> : <strong>{track.title}</strong>}
        <Typography
          component="span"
          sx={{ display: 'block', fontSize: 14, lineHeight: '19px', color: 'text.primary' }}
        >
          {track.artistName}
        </Typography>
        {(state.error || state.status === 'loading') && (
          <span role="status" className="pp-preview-status">
            {state.error ? t(state.error) : t('loading')}
          </span>
        )}
      </div>
      <IconButton
        aria-label={t(running ? 'pausePreview' : state.error ? 'retry' : 'playPreview')}
        sx={{ width: { xs: 40, sm: 44 }, height: 44, flexShrink: 0 }}
        onClick={() => webPreviewPlayer.toggle()}
      >
        <PlaybackRing />
        {running ? <Pause /> : state.error ? <Replay /> : <PlayArrow />}
      </IconButton>
      <IconButton
        aria-label={t('nextPreview')}
        disabled={state.index + 1 >= state.queue.length}
        sx={{ width: { xs: 40, sm: 44 }, height: 44, flexShrink: 0 }}
        onClick={() => webPreviewPlayer.next()}
      >
        <SkipNext />
      </IconButton>
      <IconButton
        aria-label={t('closePreview')}
        sx={{ width: { xs: 40, sm: 44 }, height: 44, flexShrink: 0 }}
        onClick={() => webPreviewPlayer.dismiss()}
      >
        <Close />
      </IconButton>
    </Box>
  );
}
